import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useSelector } from "react-redux";


const round2 = (num) =>
  Math.round((Number(num) + Number.EPSILON) * 100) / 100;

const numberFormatter = (params) => {
  if (params.value == null || params.value === "") return "";
  if (isNaN(params.value)) return params.value;
  return Number(params.value).toFixed(2);
};

const shiftOrder = {
  "Shift-I": 1,
  "Shift-II": 2,
  "Shift-III": 3
};

const shiftMap = {
  "Shift-I": "s1",
  "Shift-II": "s2",
  "Shift-III": "s3"
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB");
const TableComponentShiftReport = ({ report = [] }) => {
  const { isdarkMode } = useSelector((state) => state.darkMode);
  const color = useSelector((state) => state.ThemeChange);

  const sortedReport = useMemo(() => {
    return [...report].sort((a, b) => {
      const d1 = new Date(a.shiftDate);
      const d2 = new Date(b.shiftDate);
      if (d1.getTime() !== d2.getTime()) return d1 - d2;
      return (shiftOrder[a.shiftType] || 99) - (shiftOrder[b.shiftType] || 99);
    });
  }, [report]);

  const uniqueDates = useMemo(() => {
    return [...new Set(sortedReport.map(r => formatDate(r.shiftDate)))];
  }, [sortedReport]);
const centerCellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const columnDefs = useMemo(() => {
  const baseColumns = [
    {
      headerName: "Site Name",
      field: "sitename",
      pinned: "left",
      width: 150,
      cellStyle: centerCellStyle
    },
    {
      headerName: "Meter Name",
      field: "metername",
      pinned: "left",
      width: 180,
      cellStyle: centerCellStyle
    }
  ];

  const dateColumns = uniqueDates.map((date) => ({
    headerName: date,
    children: [
      {
        headerName: "Shift-1",
        children: [
          { headerName: "Opening", field: `${date}_s1_open`, cellStyle: centerCellStyle },
          { headerName: "Closing", field: `${date}_s1_close`, cellStyle: centerCellStyle },
          { headerName: "Consumption", field: `${date}_s1_cons`, cellStyle: centerCellStyle }
        ],
        cellStyle: centerCellStyle,
        
      },
      {
        headerName: "Shift-2",
        children: [
          { headerName: "Opening", field: `${date}_s2_open`, cellStyle: centerCellStyle },
          { headerName: "Closing", field: `${date}_s2_close`, cellStyle: centerCellStyle },
          { headerName: "Consumption", field: `${date}_s2_cons`, cellStyle: centerCellStyle }
        ]
      },
      {
        headerName: "Shift-3",
        children: [
          { headerName: "Opening", field: `${date}_s3_open`, cellStyle: centerCellStyle },
          { headerName: "Closing", field: `${date}_s3_close`, cellStyle: centerCellStyle },
          { headerName: "Consumption", field: `${date}_s3_cons`, cellStyle: centerCellStyle }
        ]
      },
      {
        headerName: "Day Consumption",
        field: `${date}_day_cons`,
        cellStyle: centerCellStyle
      }
    ]
  }));

  const totalColumn = {
    headerName: "Total Consumption",
    field: "total_consumption",
    cellStyle: centerCellStyle
  };

  return [...baseColumns, ...dateColumns, totalColumn];
}, [uniqueDates]);


  const rowData = useMemo(() => {
    const rows = {};

    report.forEach((r) => {
      if (!rows[r.meterid]) {
        rows[r.meterid] = {
          sitename: r.sitename,
          metername: r.metername,
          meterid: r.meterid,
          total_consumption: 0
        };
      }

      const date = formatDate(r.shiftDate);
      const shiftKey = shiftMap[r.shiftType];
      if (!shiftKey) return;

      rows[r.meterid][`${date}_${shiftKey}_open`] = round2(r.opening);
      rows[r.meterid][`${date}_${shiftKey}_close`] = round2(r.closing);
      rows[r.meterid][`${date}_${shiftKey}_cons`] = round2(r.consumption);

      rows[r.meterid][`${date}_day_cons`] = round2(
        (rows[r.meterid][`${date}_day_cons`] || 0) + r.consumption
      );

      rows[r.meterid].total_consumption = round2(
        rows[r.meterid].total_consumption + r.consumption
      );
    });

    return Object.values(rows);
  }, [report]);

  const pinnedBottomRowData = useMemo(() => {
    if (!rowData.length) return [];

    const totals = { metername: "TOTAL" };

    Object.keys(rowData[0]).forEach((key) => {
      if (key.endsWith("_day_cons") || key === "total_consumption") {
        totals[key] = round2(
          rowData.reduce((sum, r) => sum + (r[key] || 0), 0)
        );
      }
    });

    return [totals];
  }, [rowData]);

  return (
    <div
      className={isdarkMode ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
      style={{
        height: "80vh",
        width: "100%",
        "--ag-header-background-color": color?.themeConfig?.colorPrimary,
        "--ag-header-foreground-color": "#fff"
      }}
    >
     <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        pinnedBottomRowData={pinnedBottomRowData}
        defaultColDef={{
          resizable: true,
          sortable: true,
          suppressMenu: true,
          filter: false,
          valueFormatter: numberFormatter,
          cellStyle: params =>
            typeof params.value === "number"
              ? { textAlign: "right" }
              : null
        }}
        rowHeight={46}
        headerHeight={48}
        pagination
      />
    </div>
  );
};

export default TableComponentShiftReport;
